// src/features/cart/cartSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { CartApiService } from '../../website/service/cartApiService';

const initialState = {
  items: [], // Start with an empty array
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  cartCount: 0, // Lightweight count for navbar
  totals: {
    totalAmount: 0,
    totalCnProcurement: 0,
    totalCustomDuty: 0,
    totalInProcurement: 0,
    totalPlatformFees: 0,
    totalGst: 0,
    grandTotal: 0,
  },
  cartTotalFinalPrice: 0, // Total finalPrice from API
  cartTotalCombinedWeight: 0, // Total combined weight from API
  cartLMD: 0, // Cart-level LMD from API
  cartTotalLMD: 0 // Total LMD of the Cart's Product
};

export const fetchCartItems = createAsyncThunk('cart/fetchCartItems', async (_, { rejectWithValue }) => {
  try {
    const response = await CartApiService.getCart();

    if (response.status.code === 200) {
      // data is null when cart is empty ("No items in cart")
      const cartData = response.data?.cartData || [];
      return {
        cartData,
        cartTotalFinalPrice: response.data?.cartTotalFinalPrice || 0,
        cartTotalCombinedWeight: response.data?.cartTotalCombinedWeight || 0,
        cartLMD: response.data?.cartLMD || 0,
        cartTotalLMD: response.data?.cartTotalLMD || 0
      };
    } else {
      return rejectWithValue('Failed to fetch cart items');
    }
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

export const fetchCartCount = createAsyncThunk('cart/fetchCartCount', async (_, { rejectWithValue }) => {
  try {
    const response = await CartApiService.getCartCount();
    if (response.status.code === 200) {
      return response.data.cartItemCount || 0;
    } else {
      return rejectWithValue('Failed to fetch cart count');
    }
  } catch (error) {
    return rejectWithValue(error.message);
  }
});

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addItem: (state, action) => {
      const item = action.payload;
      const existingItem = state.items.find(i => i.id === item.id);
      if (existingItem) {
        existingItem.quantity += item.quantity;
      } else {
        state.items.push(item);
      }
    },
    setCartTotals: (state, action) => {
      state.totals = action.payload;
    },
    // Define other reducers as needed
    clearCart: (state) => {
      state.items = [];
      state.totals = {
        totalAmount: 0,
        totalCnProcurement: 0,
        totalCustomDuty: 0,
        totalInProcurement: 0,
        totalPlatformFees: 0,
        totalGst: 0,
        grandTotal: 0,
      };
    },

  },
  extraReducers: builder => {
    builder
      .addCase(fetchCartItems.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchCartItems.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = Array.isArray(action.payload.cartData) ? action.payload.cartData : [];
        state.cartCount = state.items.length;
        state.cartTotalFinalPrice = action.payload.cartTotalFinalPrice;
        state.cartTotalCombinedWeight = action.payload.cartTotalCombinedWeight;
        state.cartLMD = action.payload.cartLMD;
        state.cartTotalLMD = action.payload.cartTotalLMD;
      })
      .addCase(fetchCartItems.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchCartCount.fulfilled, (state, action) => {
        state.cartCount = action.payload;
      });
  },
});

export const { addItem, setCartTotals, clearCart } = cartSlice.actions;
export const selectCartItems = state => state.cart.items; // Ensure this matches the state structure
export const selectCartCount = state => state.cart.cartCount;
export const selectCartStatus = state => state.cart.status;
export const selectCartError = state => state.cart.error;
export const selectCartTotals = state => state.cart.totals;
export const selectCartTotalFinalPrice = state => state.cart.cartTotalFinalPrice;
export const selectCartTotalCombinedWeight = state => state.cart.cartTotalCombinedWeight;
export const selectCartLMD = state => state.cart.cartLMD;
export const cartTotalLMD = state => state.cart.cartTotalLMD;

export default cartSlice.reducer;
